import React, { useEffect, useRef, useState } from 'react';
import type { HeroUnit } from '../heroUnits';
import { Arrow, SmartLink, StatusChip, Swipe, cx, prefersReducedMotion, useReveal } from '../ui';
import { BOARDS, boardFor, type BoardData, type BoardKey, type Topic } from './data';
import { BO_CSS } from './styles';

// The maker's side of the code: the boards their team sees, scoped to one product line.
// Four boards follow the start path: who has it, what they ask, what goes wrong, what it earns.
// Shows outcomes only; never the admin navigation or data model.

const CYCLE_MS = 7000;

/* ---------- tiles ---------- */

function Kpi({ a, k, v, d, tone }: { a: string; k: string; v: string; d?: string; tone?: 'up' | 'warn' }) {
  return (
    <div className={`bo-tile bo-kpi ga-${a}`}>
      <span className="bo-k">{k}</span>
      <span className="bo-v">{v}</span>
      {d && <span className={cx('bo-d', tone === 'up' && 'is-up', tone === 'warn' && 'is-warn')}>{d}</span>}
    </div>
  );
}

function Bars({ data, label }: { data: number[]; label: string }) {
  const max = Math.max(...data);
  return (
    <div className="bo-bars" role="img" aria-label={label}>
      {data.map((n, i) => (
        <i key={i} style={{ height: `${Math.max(6, (n / max) * 100)}%`, animationDelay: `${i * 28}ms` }} className={i === data.length - 1 ? 'is-now' : undefined} />
      ))}
    </div>
  );
}

function Area({ data, label }: { data: number[]; label: string }) {
  const max = Math.max(...data) * 1.08;
  const pts = data.map((n, i) => [(i / (data.length - 1)) * 300, 92 - (n / max) * 84] as const);
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  return (
    <svg className="bo-area" viewBox="0 0 300 96" preserveAspectRatio="none" role="img" aria-label={label}>
      <defs>
        <linearGradient id="bo-area-g" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2F9E58" stopOpacity=".22" />
          <stop offset="1" stopColor="#2F9E58" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${line} L300,96 L0,96 Z`} fill="url(#bo-area-g)" />
      <path d={line} fill="none" stroke="#2F9E58" strokeWidth="2" vectorEffect="non-scaling-stroke" className="bo-area-line" />
    </svg>
  );
}

const SHADES = ['#B9B6D9', '#241E61', '#2F9E58', '#E0A33A', '#8E8BB5'];
function Split({ parts }: { parts: [string, number][] }) {
  return (
    <>
      <div className="bo-split" role="img" aria-label={parts.map(([t, n]) => `${t} ${n}%`).join(', ')}>
        {parts.map(([t, n], i) => (
          <i key={t} style={{ flexGrow: n, background: SHADES[i % SHADES.length] }} />
        ))}
      </div>
      <ul className="bo-legend">
        {parts.map(([t, n], i) => (
          <li key={t}>
            <span style={{ background: SHADES[i % SHADES.length] }} />
            {t}
            <b>{n}%</b>
          </li>
        ))}
      </ul>
    </>
  );
}

const Trend = ({ t }: { t: Topic['trend'] }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" aria-label={t === 'up' ? 'Rising' : t === 'down' ? 'Falling' : 'Steady'} className={cx('bo-trend', `is-${t}`)}>
    {t === 'flat' ? <path d="M2 6h8" /> : <path d={t === 'up' ? 'M2 9 6 4l4 5' : 'M2 3l4 5 4-5'} />}
  </svg>
);

function Tile({ title, children, className, right }: { title: string; children: React.ReactNode; className?: string; right?: React.ReactNode }) {
  return (
    <div className={cx('bo-tile', className)}>
      <div className="bo-tile-h">
        <span>{title}</span>
        {right}
      </div>
      {children}
    </div>
  );
}

/* ---------- boards ---------- */

function Owners({ d }: { d: BoardData }) {
  const o = d.owners;
  return (
    <div className="bo-grid bo-grid-owners">
      <Kpi a="a" k="Units registered" v={o.registered} d={`${o.month} this month`} tone="up" />
      <Kpi a="b" k="New owners" v={o.newOwners} d="With consent to contact" />
      <Kpi a="c" k="Second owners" v={o.secondOwners} d="Took over with consent" />
      <Tile title="Registrations, last 12 weeks" className="ga-t">
        <Bars data={o.weeks} label="Weekly registrations, rising over 12 weeks" />
      </Tile>
      <Tile title="Units by status" className="ga-s bo-opt">
        <Split parts={o.status} />
      </Tile>
      <div className="bo-tile bo-queue ga-q">
        <span className="bo-queue-dot" />
        <span>{o.pending}</span>
        <span className="bo-queue-go">
          Open <Arrow />
        </span>
      </div>
    </div>
  );
}

function Asks({ d }: { d: BoardData }) {
  const a = d.asks;
  const [added, setAdded] = useState(false);
  return (
    <div className="bo-grid bo-grid-asks">
      <Kpi a="a" k="Answered without a call" v={a.resolved} d="Each answer cites its source" tone="up" />
      <Kpi a="b" k="Owner satisfaction" v={`${a.csat}/5`} d="After each conversation" />
      <Tile title="What owners asked, this month" className="ga-t">
        <ul className="bo-topics">
          {a.topics.map((t) => (
            <li key={t.t} className={t.trend === 'up' && /Up from/.test(t.note) ? 'is-flag' : undefined}>
              <span className="bo-topic-t">{t.t}</span>
              <span className="bo-topic-n">
                <Trend t={t.trend} />
                {t.n}
              </span>
              <span className="bo-topic-s">{t.note}</span>
            </li>
          ))}
        </ul>
      </Tile>
      <div className="bo-tile bo-reco ga-r">
        <span className="bo-reco-k">Recommended</span>
        <p>
          <b>{a.gap.n}</b> {a.gap.t}.
        </p>
        <button type="button" className={cx('bo-btn', added && 'is-done')} onClick={() => setAdded(true)} aria-pressed={added}>
          {added ? 'Answer added. The next owner gets it first time.' : 'Add an answer'}
        </button>
      </div>
    </div>
  );
}

function Breaks({ d }: { d: BoardData }) {
  const b = d.breaks;
  const [sent, setSent] = useState(false);
  const lots = ['L2512', 'L2601', b.lot, 'L2609'].filter((x, i, arr) => arr.indexOf(x) === i);
  const counts = lots.map((l) => (l === b.lot ? b.fromLot : Math.max(0, Math.round((b.cases - b.fromLot) / (lots.length - 1)))));
  const max = Math.max(...counts, 1);
  return (
    <div className="bo-grid bo-grid-breaks">
      <Kpi a="a" k="Open cases" v={b.open} d="Each with serial, owner and history" />
      <Kpi a="b" k="Returns" v={b.returns} d="Flowing back to your ERP" />
      <Kpi a="c" k="Units in repair" v={b.inRepair} d="Tracked by serial" />
      <Tile title="Cases by build lot" className="ga-p" right={<span className="bo-pill is-warn">{b.cases} cases this month</span>}>
        <p className="bo-pattern">
          <b>{b.fault}</b>: {b.fromLot} of {b.cases} cases come from build lot <span className="font-mono">{b.lot}</span>.
        </p>
        <div className="bo-lots" role="img" aria-label={`Cases by build lot, ${b.lot} highest`}>
          {lots.map((l, i) => (
            <div key={l} className={cx('bo-lot', l === b.lot && 'is-hot')}>
              <i style={{ width: `${Math.max(4, (counts[i] / max) * 100)}%` }} />
              <span className="font-mono">{l}</span>
              <b>{counts[i]}</b>
            </div>
          ))}
        </div>
      </Tile>
      <div className="bo-tile bo-bulletin ga-n">
        <span className="bo-reco-k">Service bulletin</span>
        <p className="bo-bul-s font-mono">{b.serials}</p>
        <p className="bo-bul-to">{b.to}</p>
        <button type="button" className={cx('bo-btn bo-btn-dark', sent && 'is-done')} onClick={() => setSent(true)} aria-pressed={sent}>
          {sent ? `Sent to ${b.units} units` : `Notify ${b.units} units`}
        </button>
      </div>
    </div>
  );
}

const num = (v: string) => Number(v.replace(/[^0-9.]/g, ''));

function Earns({ d }: { d: BoardData }) {
  const e = d.earns;
  return (
    <div className="bo-grid bo-grid-earns">
      <Kpi a="a" k="Parts, accessories and cover" v={e.revenue} d="This quarter, from scans" tone="up" />
      <Kpi a="b" k="Orders" v={e.orders} d="Parts that fit the serial" />
      <Kpi a="c" k="Abandoned checkouts" v={e.abandoned} d="Worth a reminder" />
      <Tile title="Revenue from owners, last 6 months" className="ga-t">
        <Area data={e.months} label="Monthly revenue from owners, rising over six months" />
      </Tile>
      <Tile title="Best sellers" className="ga-s bo-opt">
        <ul className="bo-best">
          {[...e.top].sort((x, y) => num(y.v) - num(x.v)).map((t) => (
            <li key={t.t}>
              <span>
                {t.t}
                {t.sub && <small>{t.sub}</small>}
              </span>
              <b>{t.v}</b>
            </li>
          ))}
        </ul>
      </Tile>
    </div>
  );
}

const VIEWS: Record<BoardKey, React.ComponentType<{ d: BoardData }>> = { owners: Owners, asks: Asks, breaks: Breaks, earns: Earns };

/* ---------- the window ---------- */

export function BackOffice({ unit, className, cycle = true }: { unit: HeroUnit; className?: string; cycle?: boolean }) {
  const d = boardFor(unit.key);
  const [i, setI] = useState(0);
  const [auto, setAuto] = useState(cycle);
  const [hover, setHover] = useState(false);
  const [onScreen, setOnScreen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const board = BOARDS[i];
  const View = VIEWS[board.key];

  useEffect(() => {
    // Phones: the boards differ in height, so cycling would move the page under the reader's thumb.
    if (prefersReducedMotion() || window.matchMedia('(max-width: 759px)').matches) setAuto(false);
    const el = ref.current;
    if (!el || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(([e]) => setOnScreen(e.intersectionRatio >= 0.45), { threshold: [0, 0.45, 1] });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const pick = (k: number) => {
    setAuto(false);
    setI(k);
  };
  // Keep the chosen tab in view when the rail scrolls sideways (phones). Horizontal only: never move the page.
  const railRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const rail = railRef.current;
    const tab = rail?.children[i] as HTMLElement | undefined;
    if (!rail || !tab || rail.scrollWidth <= rail.clientWidth) return;
    rail.scrollTo({ left: tab.offsetLeft - 12, behavior: 'smooth' });
  }, [i]);
  const paused = !auto || hover || !onScreen;

  return (
    <div
      ref={ref}
      className={cx('bo', className)}
      style={{ '--bo-acc': unit.accent } as React.CSSProperties}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
    >
      <style>{BO_CSS}</style>
      <div className="bo-top">
        <span className="bo-mono" aria-hidden="true">
          {unit.brand.charAt(0)}
        </span>
        <span className="bo-brand">{unit.brand}</span>
        <span className="bo-scope">
          <span className="bo-scope-k">Product line</span> {d.line}
        </span>
        <span className="bo-range">Last 90 days</span>
      </div>
      <div className="bo-body">
        <div className="bo-rail" ref={railRef} role="tablist" aria-label="Boards">
          {BOARDS.map((b, k) => (
            <button
              key={b.key}
              type="button"
              role="tab"
              aria-selected={k === i}
              className={cx('bo-tab', k === i && 'is-on')}
              onClick={() => pick(k)}
            >
              <span className="bo-tab-n font-mono">{String(k + 1).padStart(2, '0')}</span>
              <span className="bo-tab-q">{b.q}</span>
              <span className="bo-tab-l">{b.label}</span>
              {k === i && auto && (
                <i
                  key={`f-${i}`}
                  className="bo-tab-fill"
                  aria-hidden="true"
                  style={{ animationDuration: `${CYCLE_MS}ms`, animationPlayState: paused ? 'paused' : 'running' }}
                  onAnimationEnd={() => setI((x) => (x + 1) % BOARDS.length)}
                />
              )}
            </button>
          ))}
          <p className="bo-rail-note">Arrange your own boards from KPIs, trends, splits and work queues.</p>
        </div>
        <div className="bo-canvas" role="tabpanel" aria-label={board.q}>
          <div className="bo-canvas-h">
            <h3>{board.q}</h3>
            {board.status !== 'live' && <StatusChip status={board.status} />}
          </div>
          <div key={`${unit.key}-${board.key}`} className="bo-view">
            <View d={d} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- the section ---------- */

export function BackOfficeSection({ unit, id = 'your-side', tone = 'alt' }: { unit: HeroUnit; id?: string; tone?: 'alt' | 'white' }) {
  const ref = useReveal<HTMLElement>();
  return (
    <section className={cx('section bo-sec', tone === 'alt' && 'bg-bm-alt')} id={id} ref={ref}>
      <div className="wrap">
        <div className="bo-sec-head" data-reveal>
          <div>
            <h2 className="t-h2">
              Who has it?<br /> What goes wrong?<br /> <Swipe>What does it earn?</Swipe>
            </h2>
          </div>
          <p className="t-lede">
            Every scan, question, claim and order lands on its serial. Your team sees the answers on one board per product line.
          </p>
        </div>
        <div data-reveal>
          <BackOffice unit={unit} />
        </div>
        <div className="bo-sec-foot" data-reveal>
          <p>Example boards for a fictional maker. Yours use your own products and numbers.</p>
          <SmartLink href="/platform#back-office" className="vc-link">
            What your team sees <Arrow />
          </SmartLink>
        </div>
      </div>
    </section>
  );
}
